import { TypedDocumentNode, gql } from '@apollo/client';
import { MessageDescriptor, useIntl } from 'react-intl';

import { SHARING_ORIGIN } from '@sorare/core/src/config';
import { ANY_SPORT_PLAY_PRO_LINEUPS_ID } from '@sorare/core/src/constants/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import idFromObject from '@sorare/core/src/gql/idFromObject';
import { UTM_CAMPAIGNS } from '@sorare/core/src/hooks/useUtmParams';
import { randomElement } from '@sorare/core/src/lib/arrays';
import { withFragments } from '@sorare/core/src/lib/gql';

import {
  isFixtureStarted,
  notStartedLineupSharingMessages,
  startedLineupSharingMessages,
} from 'lib/so5';

import {
  generateLineupSharingTitle_so5Fixture,
  useLineupSharingAttributes_so5Lineup,
} from './__generated__/useLineupSharingAttributes.graphql';

const generateLineupSharingTitle = withFragments(
  (fixture: generateLineupSharingTitle_so5Fixture): MessageDescriptor => {
    const messages = isFixtureStarted(fixture)
      ? startedLineupSharingMessages
      : notStartedLineupSharingMessages;
    return randomElement([messages.set1, messages.set2, messages.set3]);
  },
  {
    so5Fixture: gql`
      fragment generateLineupSharingTitle_so5Fixture on So5Fixture {
        slug
        aasmState
      }
    ` as TypedDocumentNode<generateLineupSharingTitle_so5Fixture>,
  }
);

export const useLineupSharingAttributes = (
  so5Lineup?: useLineupSharingAttributes_so5Lineup | null
) => {
  const { formatMessage } = useIntl();
  const { currentUser } = useCurrentUserContext();
  const { generateSportContextPath } = useSportContext();

  if (!so5Lineup) return {};

  return {
    url:
      SHARING_ORIGIN +
      generateSportContextPath(ANY_SPORT_PLAY_PRO_LINEUPS_ID, {
        params: { id: idFromObject(so5Lineup.id) },
      }),
    message: (link?: string) =>
      formatMessage(generateLineupSharingTitle(so5Lineup.so5Fixture), {
        displayName: so5Lineup.so5Leaderboard?.displayName,
        clubName: currentUser?.footballUserProfile?.clubName,
        link,
      }),
    sharedItem: UTM_CAMPAIGNS.LINEUP,
    sharedItemId: idFromObject(so5Lineup.id),
  };
};

useLineupSharingAttributes.fragments = {
  so5Lineup: gql`
    fragment useLineupSharingAttributes_so5Lineup on So5Lineup {
      id
      so5Fixture {
        slug
        ...isFixtureStarted_so5Fixture
        ...generateLineupSharingTitle_so5Fixture
      }
      so5Leaderboard {
        slug
        displayName
      }
    }
    ${isFixtureStarted.fragments.so5Fixture}
    ${generateLineupSharingTitle.fragments.so5Fixture}
  ` as TypedDocumentNode<useLineupSharingAttributes_so5Lineup>,
};
